import React, {
  ForwardedRef,
  RefObject,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import useOnViewport from 'src/hooks/useOnViewport';
import { usePopupController } from 'src/hooks/usePopupController';

export interface VideoPlayerProps
  extends React.DetailedHTMLProps<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    HTMLVideoElement
  > {
  videoLabel: string;
  pausePopupWhenStopped?: boolean;
}

const VideoPlayer = (
  { children, pausePopupWhenStopped = false, ...rest }: VideoPlayerProps,
  ref: ForwardedRef<HTMLVideoElement>
): JSX.Element => {
  const videoTagRef = ref ?? useRef<HTMLVideoElement>();
  const isInViewPort = useOnViewport(videoTagRef as RefObject<HTMLElement>);
  const [videoWatched] = useState(false);

  const { pause, resume } = usePopupController();

  const onVideoBeingWatched = useCallback(() => {
    // TODO: Re-enable Video Tracking if the analytics team decides to use it
    // const currentTime = (videoTagRef as RefObject<HTMLVideoElement>).current.currentTime;
    // if (isInViewPort && currentTime > 5 && !videoWatched) {
    //   setVideoWatched(true);
    //   gTagEvent('Video_Being_Watched', {
    //     category: 'Video Player Wrapper',
    //     label: videoLabel,
    //   });
    // }
  }, [isInViewPort, videoWatched]);

  const onVideoEnded = useCallback(() => {
    // TODO: Re-enable Video Tracking if the analytics team decides to use it
    // if (isInViewPort) {
    //   gTagEvent('Video_Ended', {
    //     category: 'Video Player Wrapper',
    //     label: videoLabel,
    //   });
    // }
  }, [isInViewPort]);

  const onVideoStarted = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      if (rest.onLoadStart) rest.onLoadStart(event);
      // TODO: Re-enable Video Tracking if the analytics team decides to use it
      // if (isInViewPort) {
      //   gTagEvent('Video_Started', {
      //     category: 'Video Player Wrapper',
      //     label: videoLabel,
      //   });
      // }
    },
    [isInViewPort]
  );

  // Resume popup countdown when video is paused
  const handleVideoPause = (
    event: React.SyntheticEvent<HTMLVideoElement, Event>
  ): void => {
    if (rest.onPause) rest.onPause(event);
    if (pausePopupWhenStopped) resume();
  };

  // Pause popup countdown when video is playing
  const handleVideoPlay = (
    event: React.SyntheticEvent<HTMLVideoElement, Event>
  ): void => {
    if (rest.onPlay) rest.onPlay(event);
    if (pausePopupWhenStopped) pause();
  };

  // Resume popup countdown when video end
  const handleVideoEndedCapture = (
    event: React.SyntheticEvent<HTMLVideoElement, Event>
  ): void => {
    if (rest.onEndedCapture) rest.onEndedCapture(event);
    if (pausePopupWhenStopped) resume();
  };

  // TODO: re-add or remove videoLabel from rest based on the analytics team's decision
  delete rest.videoLabel;

  return (
    <video
      {...rest}
      ref={videoTagRef}
      onLoadStart={(e) => onVideoStarted(e)}
      onEnded={() => onVideoEnded()}
      onTimeUpdate={() => onVideoBeingWatched()}
      onPlay={handleVideoPlay}
      onPause={handleVideoPause}
      onEndedCapture={handleVideoEndedCapture}
    >
      {children}
    </video>
  );
};

export default forwardRef(VideoPlayer);
